import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useStatus } from '../context/StatusContext';
import LoadingAnimation from '../components/loadingAnimation';
import { useDataCache } from '../hooks/useDataCache';
import { cacheManager } from '../hooks/GlobalCacheManager';
import '../styles/FTSOPerformance.css';
import '../styles/loading.css';
import { getApiBase } from '../utils/apiBase';
import { useDataUpdates } from '../hooks/useDataUpdates';

const FTSOPerformance = () => {
  const [data, setData] = useState({});
  const [selectedFeed, setSelectedFeed] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('flare');
  const [availableFeeds, setAvailableFeeds] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartRef = useRef(null);
  const dataRef = useRef(null);
  const [refreshing, setRefreshing] = useState(false);
  const { updatePageStatus } = useStatus();
  const { getCachedData } = useDataCache();
  const [visibleSeries, setVisibleSeries] = useState({
    bounds: true,
    submissions: true,
    datapoints: false,
  });

  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    zoomPlugin
  );

  const FTSO_PERFORMANCE_ENDPOINTS = [];


  const handleSeriesToggle = (series) => {
    setVisibleSeries((prev) => ({
      ...prev,
      [series]: !prev[series],
    }));
  };

  const colors = {
    UYS_flare: '#8884d8',
    UYS_songbird: '#82ca9d',
    ENV_flare: '#ffc658',
    ENV_songbird: '#ff7300',
    bounds_flare_lower: 'rgba(136, 132, 216, 0.2)',
    bounds_flare_upper: 'rgba(136, 132, 216, 0.2)',
    bounds_songbird_lower: 'rgba(130, 202, 157, 0.2)',
    bounds_songbird_upper: 'rgba(130, 202, 157, 0.2)',
  };

  // Ensure newest round has bounds (results) for the selected network to avoid bad y-scale
  const hasValidLatestRound = useCallback((dataObj, network) => {
    try {
      const uysNet = dataObj?.UYS?.[network];
      if (!uysNet || typeof uysNet !== 'object') return false;
      const rounds = Object.keys(uysNet).map(r => parseInt(r, 10)).filter(n => Number.isFinite(n));
      if (rounds.length === 0) return false;
      const latest = Math.max(...rounds);
      const feeds = uysNet[latest];
      if (!Array.isArray(feeds)) return false;
      // Require at least one feed with non-zero bounds
      return feeds.some(f => Number(f?.lower_bound) > 0 && Number(f?.upper_bound) > 0);
    } catch {
      return false;
    }
  }, []);

  const handleRefreshData = async () => {
    try {
      setRefreshing(true);
  
      // Check cache first
      const cachedData = {
        submit2Data: {}
      };
  
      // If cache is valid, use it (disabled: no submit2prices)
      if (false) {
        console.log('Using cached FTSO performance data');
        if (hasValidLatestRound(cachedData.submit2Data, selectedNetwork)) {
          setData(cachedData.submit2Data);
        } else {
          console.log('Skipped cached update: latest round missing results/bounds');
        }
  
        // Update available feeds
        if (Object.keys(cachedData.submit2Data).length > 0) {
          const firstEntity = Object.keys(cachedData.submit2Data)[0];
          const firstNetwork = Object.keys(cachedData.submit2Data[firstEntity])[0];
          const firstRound = Object.keys(cachedData.submit2Data[firstEntity][firstNetwork])[0];
          const feeds = cachedData.submit2Data[firstEntity][firstNetwork][firstRound].map(
            (item) => item.feed
          );
          const uniqueFeeds = [...new Set(feeds)].sort();
          setAvailableFeeds(uniqueFeeds);
  
          if (!selectedFeed) {
            setSelectedFeed(uniqueFeeds[0]);
          }
        }
  
        setError(null);
        setRefreshing(false);
        return;
      }

      // Disabled submit2prices network fetch
      const newData = {};
      if (hasValidLatestRound(newData, selectedNetwork)) {
        setData(newData);
      } else {
        console.log('Skipped network update: latest round missing results/bounds');
      }

      // Update available feeds
      if (Object.keys(newData).length > 0) {
        const firstEntity = Object.keys(newData)[0];
        const firstNetwork = Object.keys(newData[firstEntity])[0];
        const firstRound = Object.keys(newData[firstEntity][firstNetwork])[0];
        const feeds = newData[firstEntity][firstNetwork][firstRound].map(
          (item) => item.feed
        );
        const uniqueFeeds = [...new Set(feeds)].sort();
        setAvailableFeeds(uniqueFeeds);

        if (!selectedFeed) {
          setSelectedFeed(uniqueFeeds[0]);
        }
      }


      setError(null);
    } catch (error) {
      console.error('Error loading data:', error);
      setError(`Failed to load data: ${error.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    console.log('FTSO Performance component mounted');
    handleRefreshData();
    return () => {
      console.log('FTSO Performance component unmounting');
    };
  }, []);

  useEffect(() => {
    cacheManager.registerEndpoints('ftsoPerformance', FTSO_PERFORMANCE_ENDPOINTS);
    return () => cacheManager.unregisterEndpoints('ftsoPerformance');
  }, []);

  const handleDataUpdate = useCallback((newData) => {
    // Guard against updates that would include a newest round without results
    if (!hasValidLatestRound(newData, selectedNetwork)) {
      return;
    }
    const dataChanged = JSON.stringify(newData) !== JSON.stringify(dataRef.current);
  
    if (dataChanged) {
      dataRef.current = newData;
      setData(newData);
  
      if (Object.keys(newData).length > 0) {
        const firstEntity = Object.keys(newData)[0];
        const firstNetwork = Object.keys(newData[firstEntity])[0];
        const firstRound = Object.keys(newData[firstEntity][firstNetwork])[0];
        const feeds = newData[firstEntity][firstNetwork][firstRound].map(
          (item) => item.feed
        );
        const uniqueFeeds = [...new Set(feeds)].sort();
        setAvailableFeeds(uniqueFeeds);
  
        if (!selectedFeed) {
          setSelectedFeed(uniqueFeeds[0]);
        }
      }
    }
  }, [selectedFeed, hasValidLatestRound, selectedNetwork]);

  const loadLatestData = useCallback(async (opts = { showLoading: true }) => {
    try {
      if (opts.showLoading) setLoading(true);
  
      // Disabled submit2prices network fetch
      const newData = {};
      if (hasValidLatestRound(newData, selectedNetwork)) {
        handleDataUpdate(newData);
      } else {
        console.log('Skipped SSE/network update: latest round missing results/bounds');
      }
      setError(null);
      updatePageStatus('ftsoPerformance', 'green');
  
    } catch (error) {
      console.error('Error loading data:', error);
      setError(`Failed to load data: ${error.message}`);
      updatePageStatus('ftsoPerformance', 'red');
    } finally {
      if (opts.showLoading) setLoading(false);
    }
  }, [getCachedData, updatePageStatus, hasValidLatestRound, selectedNetwork, handleDataUpdate]);


  useEffect(() => {
    console.log('FTSO Performance component mounted');
    loadLatestData();
  }, [loadLatestData]);

  // Disable SSE-driven submit2prices updates

  useEffect(() => {
    if (data && selectedFeed) {
      const processedData = [];
      const allRounds = new Set();
      const combinations = [
        { entity: 'UYS', network: 'flare' },
        { entity: 'ENV', network: 'flare' },
        { entity: 'UYS', network: 'songbird' },
        { entity: 'ENV', network: 'songbird' },
      ];
  
      // Collect all rounds
      combinations.forEach(({ entity, network }) => {
        if (data[entity]?.[network]) {
          Object.keys(data[entity][network]).forEach((round) => {
            allRounds.add(parseInt(round));
          });
        }
      });
  
      // Build processedData with submit2prices data only
      Array.from(allRounds)
        .sort((a, b) => a - b)
        .forEach((round) => {
          const dataPoint = { round: round.toString() };
  
          combinations.forEach(({ entity, network }) => {
            if (data[entity]?.[network]?.[round]) {
              const feedData = data[entity][network][round].find(
                (item) => item.feed === selectedFeed
              );
              if (feedData) {
                dataPoint[`${entity}_${network}`] = feedData.price;
                dataPoint[`${entity}_${network}_result`] = feedData.result;
                if (entity === 'UYS') {
                  dataPoint[`bounds_${network}_lower`] = feedData.lower_bound;
                  dataPoint[`bounds_${network}_upper`] = feedData.upper_bound;
                }
              }
            }
          });
  
          processedData.push(dataPoint);
        });
      
      setChartData(processedData);
    }
  }, [data, selectedFeed]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.015)',
        },
        ticks: {
          stepSize: 1,
          callback: function(value) {
            if (Number.isInteger(value)) {
              return Math.floor(value);
            }
            return '';
          }
        }
      },
      y: {
        title: {
          display: true,
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.015)',
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function(context) {
            return `Round: ${context[0].raw.x.toString().replace(/,/g, '')}`;
          },
          label: function(context) {
            const label = context.dataset.label;
            const value = context.raw.y;
            
            if (label.includes('bounds')) {
              return `${label}: ${value?.toFixed(6) || 'N/A'}`;
            }
            
            // For submissions, show value, result and hit status
            const resultKey = `${label}_result`;
            const result = context.raw[resultKey];
            let hitStatus = '';
            if (result === 'hit') {
              hitStatus = ' ✓';
            } else if (result === 'miss') {
              hitStatus = ' ✗';
            }
            
            return `${label}: ${value?.toFixed(6) || 'N/A'}${hitStatus}`;
          }
        }
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: 'x',
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'xy',
      intersect: true,
    },
  };

  const chartDatasets = [
    // Submission data
    ...(visibleSeries.submissions
      ? ['UYS', 'ENV'].map((entity) => ({
          label: `${entity}_${selectedNetwork}`,
          data: chartData.map((d) => ({
            x: d.round,
            y: d[`${entity}_${selectedNetwork}`],
            [`${entity}_${selectedNetwork}_result`]:
              d[`${entity}_${selectedNetwork}_result`],
          })),
          borderColor: colors[`${entity}_${selectedNetwork}`],
          backgroundColor: colors[`${entity}_${selectedNetwork}`],
          borderWidth: 0,
          pointStyle: 'rectRot',
          pointRadius: 6,
          tension: 0.1,
        }))
      : []),

    // Bounds data
    ...(visibleSeries.bounds
      ? ['lower', 'upper'].map((bound) => ({
          label: `bounds_${selectedNetwork}_${bound}`,
          data: chartData.map((d) => ({
            x: d.round,
            y: d[`bounds_${selectedNetwork}_${bound}`],
          })),
          borderColor: colors[`bounds_${selectedNetwork}_${bound}`],
          backgroundColor: colors[`bounds_${selectedNetwork}_${bound}`],
          borderWidth: 1,
          pointRadius: 0,
          fill: '+1',
          tension: 0.1,
        }))
      : []),

  ];

  if (loading) {
    return (
      <div className="loading-container">
        <LoadingAnimation />
      </div>
    );
  }
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="ftso-performance-container">
      <div className="controls">
        <div className="feed-selector">
          <select
            value={selectedFeed}
            onChange={(e) => setSelectedFeed(e.target.value)}
          >
            {availableFeeds.map((feed) => (
              <option key={feed} value={feed}>
                {feed}
              </option>
            ))}
          </select>
        </div>

        <div className="series-toggles">
          <div className="network-toggle">
              <button
                className={selectedNetwork === 'flare' ? 'active' : ''}
                onClick={() => setSelectedNetwork('flare')}
              >
                Flare
              </button>
              <button
                className={selectedNetwork === 'songbird' ? 'active' : ''}
                onClick={() => setSelectedNetwork('songbird')}
              >
                Songbird
              </button>
              <div className="slider"></div>
            </div>

          <label className="series-toggle submissions">
            <input
              type="checkbox"
              checked={visibleSeries.submissions}
              onChange={() => handleSeriesToggle('submissions')}
            />
            Submissions
          </label>

          <label className="series-toggle bounds">
            <input
              type="checkbox"
              checked={visibleSeries.bounds}
              onChange={() => handleSeriesToggle('bounds')}
            />
            Bounds
          </label>

        </div>
        <div className="button-controls">
          <button
            className="button"
            onClick={() => {
              const chartInstance = chartRef.current;
              if (chartInstance) {
                chartInstance.resetZoom();
              }
            }}
          >
            Reset Zoom
          </button>
          <button
            className="button"
            onClick={handleRefreshData}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>        </div>
      </div>
      <div className="chart-container">
        <Line
          ref={chartRef}
          data={{ datasets: chartDatasets }}
          options={chartOptions}
        />
      </div>
    </div>
  );
};

export default FTSOPerformance;
